/**
 * الأعداد العامة — العدد الكامل والمبدل والباقي، سجل تنظيمي عام
 * منفصل تماماً عن معايير الاستحقاق لكل صنف (التبويب الآخر)
 */
import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import ExportButton from "../../components/shared/ExportButton";
import { downloadApiFile } from "../../api/download";
import PrintLayout from "../../components/Layout/PrintLayout";
import { formatDate, formatDateLong, formatNumber } from "../../lib/date";
import type { HeadcountAsOf, Governorate, Directorate } from "../../types";
import { UNIT_TYPE_LABELS } from "../../types";

type Field = "fullCount" | "allowanceCount";

export default function GeneralHeadcountTab({ asOf, govId }: { asOf: string; govId: string }) {
  const { isRole } = useAuth();
  const qc = useQueryClient();
  const canEdit = isRole("SYSTEM_ADMIN");

  const [active, setActive] = useState<{ dirId: number; field: Field } | null>(null);
  const [value,  setValue]  = useState("");
  const [error,  setError]  = useState("");

  const { data: dirs = [] } = useQuery({
    queryKey: ["directorates", "active"],
    queryFn: () => api.get<{ data: Directorate[] }>("/directorates?isActive=true").then((r) => r.data.data),
  });
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["headcount-as-of", asOf],
    enabled: !!asOf,
    queryFn: () => api.get<{ data: HeadcountAsOf[] }>(`/org-headcounts/as-of?asOf=${asOf}`).then((r) => r.data.data),
  });

  const byDir = useMemo(() => new Map(rows.map((r) => [r.directorateId, r])), [rows]);
  const visible = govId ? dirs.filter((d) => d.governorateId === Number(govId)) : dirs;

  const totals = visible.reduce(
    (t, d) => {
      const r = byDir.get(d.id);
      return r ? { full: t.full + r.fullCount, allow: t.allow + r.allowanceCount } : t;
    },
    { full: 0, allow: 0 }
  );

  const open = (dirId: number, field: Field) => {
    if (!canEdit) return;
    setActive({ dirId, field });
    setValue(String(byDir.get(dirId)?.[field] ?? 0));
    setError("");
  };

  const save = async () => {
    if (!active) return;
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) { setError("أدخل رقماً صحيحاً ≥ 0"); return; }

    const existing  = byDir.get(active.dirId);
    const full      = active.field === "fullCount"      ? num : (existing?.fullCount      ?? 0);
    const allowance = active.field === "allowanceCount" ? num : (existing?.allowanceCount ?? 0);
    if (allowance > full) { setError("عدد المبدل لا يمكن أن يتجاوز العدد الكامل"); return; }
    if (existing && existing.fullCount === full && existing.allowanceCount === allowance) {
      setActive(null); return;
    }

    try {
      if (existing && existing.effectiveFrom === asOf) {
        await api.put(`/org-headcounts/${existing.id}`, { fullCount: full, allowanceCount: allowance });
      } else {
        await api.post("/org-headcounts", {
          effectiveFrom: asOf, directorateId: active.dirId, fullCount: full, allowanceCount: allowance,
        });
      }
      await qc.invalidateQueries({ queryKey: ["headcount-as-of"] });
      setActive(null);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "حدث خطأ أثناء الحفظ"
      );
    }
  };

  const cell = (d: Directorate, field: Field) => {
    const r = byDir.get(d.id);
    if (active?.dirId === d.id && active.field === field) {
      return (
        <input type="number" min="0" autoFocus dir="ltr"
          className="w-24 text-center text-sm border-2 border-primary-700 rounded px-1 py-1 focus:outline-none"
          value={value} onChange={(e) => setValue(e.target.value)}
          onBlur={() => void save()}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); void save(); }
            if (e.key === "Escape") { setActive(null); setError(""); }
          }} />
      );
    }
    return (
      <span dir="ltr" onClick={() => open(d.id, field)}
        className={`inline-block min-w-[4rem] px-2 py-1 rounded ${canEdit ? "cursor-pointer hover:bg-primary-50" : ""} ${r ? "" : "text-sand-300"}`}>
        {r ? formatNumber(r[field], 0) : "—"}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-end mb-3 no-print">
        <ExportButton onExport={() => void downloadApiFile(`/org-headcounts/as-of?asOf=${asOf}&format=csv`)} />
      </div>

      {error && <p className="text-sm text-red-700 font-medium mb-3">{error}</p>}

      <div className="alert-info mb-4 no-print text-xs leading-relaxed">
        <strong>ما هذه الصفحة؟</strong> لكل مديرية أو فرع عدد أفراد كامل، وعدد من اختار
        البدل النقدي بدل الوجبات. <strong>الباقي</strong> = الكامل − المبدل — سجل تنظيمي عام،
        منفصل عن معياري استحقاق كل صنف (تبويب "معايير الاستحقاق").
        {canEdit && (
          <>
            <br />
            انقر رقماً للتعديل — Enter للحفظ، Esc للإلغاء. القيمة الجديدة تسري اعتباراً من{" "}
            <strong dir="ltr">{formatDate(asOf)}</strong>، والقيم السابقة تبقى محفوظة لتواريخها.
          </>
        )}
      </div>

      <PrintLayout title="الأعداد العامة" subtitle={`ساري بتاريخ ${formatDateLong(asOf)}`}>
        {isLoading ? (
          <p className="text-center text-sand-500 py-8">جاري التحميل...</p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>المديرية / الفرع</th>
                  <th>النوع</th>
                  <th>المحافظة</th>
                  <th className="text-center">العدد الكامل</th>
                  <th className="text-center">المبدل</th>
                  <th className="text-center">الباقي</th>
                  <th className="text-center text-xs">ساري من</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((d) => {
                  const r = byDir.get(d.id);
                  return (
                    <tr key={d.id}>
                      <td className="font-medium">{d.nameAr}</td>
                      <td className="text-xs text-sand-500">{UNIT_TYPE_LABELS[d.unitType]}</td>
                      <td>{d.governorate?.nameAr}</td>
                      <td className="text-center">{cell(d, "fullCount")}</td>
                      <td className="text-center">{cell(d, "allowanceCount")}</td>
                      <td className="text-center font-semibold" dir="ltr">
                        {r ? formatNumber(r.remaining, 0) : "—"}
                      </td>
                      <td className="text-center text-xs text-sand-500" dir="ltr">
                        {r ? formatDate(r.effectiveFrom) : ""}
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-sand-500 py-6">لا توجد مديريات مفعّلة</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="font-bold bg-sand-50">
                  <td colSpan={3} className="px-4 py-3">الإجمالي</td>
                  <td className="text-center" dir="ltr">{formatNumber(totals.full, 0)}</td>
                  <td className="text-center" dir="ltr">{formatNumber(totals.allow, 0)}</td>
                  <td className="text-center" dir="ltr">{formatNumber(totals.full - totals.allow, 0)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </PrintLayout>
    </div>
  );
}
